import { getCsrfToken } from '@/integrations/php/client';
import type { LeadStageType, ServiceType } from '@/context/LeadContext';

export type PartnerOverviewRange = '7d' | '30d' | '90d' | '1y' | 'all';

export interface AdminPartnerOverview {
  partner: {
    id: string;
    name: string;
    phone: string;
    email: string;
    location: string;
    services: ServiceType[];
    active: boolean;
    latitude: number | null;
    longitude: number | null;
    radius: number | null;
    smsOptOut: boolean;
    miniImageUrl: string | null;
    miniDescription: string | null;
    routingReady: boolean;
    portalReady: boolean;
    lastLoginAt: string | null;
    createdAt: string | null;
    updatedAt: string | null;
  };
  summary: {
    totalLeads: number;
    newLeads: number;
    pendingLeads: number;
    informationLeads: number;
    closedLeads: number;
    viewedLeads: number;
    staleLeads: number;
    newUnviewed: number;
    closeRate: number;
    viewRate: number;
    averageCloseDays: number | null;
    lastLeadAt: string | null;
    lastActivityAt: string | null;
  };
  services: Array<{ name: ServiceType; total: number; closed: number }>;
  notifications: Record<'email' | 'sms', { sent: number; inProgress: number; failed: number; suppressed: number }>;
  mini: { views: number; callClicks: number; mapsClicks: number; interactions: number; interactionRate: number };
  monthly: Array<{ month: string; total: number; closed: number }>;
  priorityLeads: Array<{
    id: string;
    name: string;
    phone: string;
    address: string;
    postalCode: string;
    service: ServiceType;
    timeframe: string;
    stage: LeadStageType;
    viewed: boolean;
    priorityScore: number;
    createdAt: string | null;
    updatedAt: string | null;
  }>;
  billingLeads: Array<{ id: string; name: string; service: ServiceType; createdAt: string }>;
  range: PartnerOverviewRange;
  generatedAt: string;
}

const number = (value: unknown): number => Number(value || 0);
const nullableNumber = (value: unknown): number | null => value === null || value === undefined || value === '' ? null : Number(value);
const nullableString = (value: unknown): string | null => value === null || value === undefined || value === '' ? null : String(value);

export async function fetchAdminPartnerOverview(partnerId: string, range: PartnerOverviewRange): Promise<AdminPartnerOverview> {
  const params = new URLSearchParams({ partner_id: partnerId, range });
  const response = await fetch(`/api/admin/partner-overview.php?${params}`, {
    credentials: 'include',
    headers: { 'X-CSRF-Token': await getCsrfToken() },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body?.error?.message || 'Δεν φορτώθηκε ο συνεργάτης.');

  const data = body?.data || {};
  const partner = data.partner || {};
  const summary = data.summary || {};
  const notification = (channel: 'email' | 'sms') => {
    const row = data.notifications?.[channel] || {};
    return { sent: number(row.sent), inProgress: number(row.in_progress), failed: number(row.failed), suppressed: number(row.suppressed) };
  };

  return {
    partner: {
      id: String(partner.id || ''), name: String(partner.name || ''), phone: String(partner.phone || ''), email: String(partner.email || ''),
      location: String(partner.location || ''), services: Array.isArray(partner.services) ? partner.services as ServiceType[] : [],
      active: Boolean(partner.active), latitude: nullableNumber(partner.latitude), longitude: nullableNumber(partner.longitude), radius: nullableNumber(partner.radius),
      smsOptOut: Boolean(partner.sms_opt_out), miniImageUrl: nullableString(partner.mini_image_url), miniDescription: nullableString(partner.mini_description),
      routingReady: Boolean(partner.routing_ready), portalReady: Boolean(partner.portal_ready), lastLoginAt: nullableString(partner.last_login_at),
      createdAt: nullableString(partner.created_at), updatedAt: nullableString(partner.updated_at),
    },
    summary: {
      totalLeads: number(summary.total_leads), newLeads: number(summary.new_leads), pendingLeads: number(summary.pending_leads),
      informationLeads: number(summary.information_leads), closedLeads: number(summary.closed_leads), viewedLeads: number(summary.viewed_leads),
      staleLeads: number(summary.stale_leads), newUnviewed: number(summary.new_unviewed), closeRate: number(summary.close_rate),
      viewRate: number(summary.view_rate), averageCloseDays: nullableNumber(summary.average_close_days), lastLeadAt: nullableString(summary.last_lead_at),
      lastActivityAt: nullableString(summary.last_activity_at),
    },
    services: Array.isArray(data.services) ? data.services.map((row: Record<string, unknown>) => ({ name: String(row.name || 'άλλο') as ServiceType, total: number(row.total), closed: number(row.closed) })) : [],
    notifications: { email: notification('email'), sms: notification('sms') },
    mini: {
      views: number(data.mini?.views), callClicks: number(data.mini?.call_clicks), mapsClicks: number(data.mini?.maps_clicks),
      interactions: number(data.mini?.interactions), interactionRate: number(data.mini?.interaction_rate),
    },
    monthly: Array.isArray(data.monthly) ? data.monthly.map((row: Record<string, unknown>) => ({ month: String(row.month || ''), total: number(row.total), closed: number(row.closed) })) : [],
    priorityLeads: Array.isArray(data.priority_leads) ? data.priority_leads.map((row: Record<string, unknown>) => ({
      id: String(row.id || ''), name: String(row.name || ''), phone: String(row.phone || ''), address: String(row.address || ''),
      postalCode: String(row.postal_code || ''), service: String(row.service || 'άλλο') as ServiceType, timeframe: String(row.timeframe || ''),
      stage: String(row.stage || 'Νέο') as LeadStageType, viewed: Boolean(row.viewed), priorityScore: number(row.priority_score),
      createdAt: nullableString(row.created_at), updatedAt: nullableString(row.updated_at),
    })) : [],
    billingLeads: Array.isArray(data.billing_leads) ? data.billing_leads.map((row: Record<string, unknown>) => ({
      id: String(row.id || ''), name: String(row.name || ''), service: String(row.service || 'άλλο') as ServiceType, createdAt: String(row.created_at || ''),
    })) : [],
    range: (data.range || range) as PartnerOverviewRange,
    generatedAt: String(data.generated_at || ''),
  };
}
